import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { CacheAdapter } from 'src/app/adapters/cache/cache.adapter';
import { mapUrlAndQueryToKey } from 'src/app/adapters/cache/utils/cache.utils';
import { EnvironmentAdapter } from 'src/app/adapters/environment/environment.adapter';
import { MSEndpoint, MSHttpEvent, MSQuery, MSServerOkResponse } from 'src/app/adapters/http/utils/http.utils';
import { SECONDS } from 'src/app/utils/date-time.utils';
import { Environment } from 'src/environments/environment.utils';
import { MSApiHttpUseCase } from '../http/msapi-http.use-case';
import { getMSApiUrl } from '../utils/http.utils';

@Injectable({
  providedIn: 'root'
})
export class MSApiCachedGetUseCase {
  constructor(
    private cacheAdapter: CacheAdapter,
    private environmentAdapter: EnvironmentAdapter,
    private httpUseCase: MSApiHttpUseCase
  ) {}

  /**
   * This function retrieves data from an API endpoint, caches it if possible, and returns an
   * observable of the data.
   * @param typeConstructor - A constructor function for the type of data you want to retrieve. It is
   * used to create an instance of the type when needed.
   * @param expiration - The `expiration` parameter represents the duration in seconds for
   * which the cached data should be considered valid before it expires.
   * @param endpoint - The `endpoint` parameter is the endpoint of the API that you want
   * to call. It specifies the specific resource or action that you want to interact with.
   * @param [query] - The `query` parameter is an optional object that represents the query
   * parameters to be included in the HTTP request. It can be used to specify additional information
   * or filters for the request.
   * @returns an Observable of type `MSHttpEvent.Spec<Type>`.
   */
  get(expiration: SECONDS, endpoint: MSEndpoint, query?: MSQuery): Observable<MSHttpEvent.Spec> {
    const valueObservable = this.getKeyObservable(endpoint, query).pipe(
      mergeMap((key) => forkJoin([this.cacheAdapter.get(key), of(key)])),
      mergeMap(([value, key]) => this.handleCacheReponse(value, key, expiration, endpoint, query))
    );

    return valueObservable;
  }

  private getKeyObservable(endpoint: MSEndpoint, query?: MSQuery): Observable<string> {
    const keyObservable = this.environmentAdapter.getEnvironment().pipe(
      map((environment: Environment) => {
        const url = getMSApiUrl(environment.serverUrl, endpoint);
        const key = mapUrlAndQueryToKey(url, query);

        return key;
      })
    );

    return keyObservable;
  }

  private handleHttpGetResponse(responseEvent: MSHttpEvent.Spec, key: string, expiration: SECONDS): Observable<MSHttpEvent.Spec> {
    const shouldSaveToCache = responseEvent instanceof MSHttpEvent.Success;
    if (shouldSaveToCache) {
      return this.cacheAdapter.set(responseEvent.data, key, expiration).pipe(map(() => responseEvent));
    }

    const httpValueObservable = of(responseEvent);
    return httpValueObservable;
  }

  private handleCacheReponse(
    value: Object,
    key: string,
    expiration: SECONDS,
    endpoint: MSEndpoint,
    query?: MSQuery
  ): Observable<MSHttpEvent.Spec> {
    const shouldGetFromHttp = value == null;
    if (shouldGetFromHttp) {
      return this.httpUseCase
        .get(endpoint, query)
        .pipe(mergeMap((responseEvent) => this.handleHttpGetResponse(responseEvent, key, expiration)));
    }

    const msSuccessEvent = new MSHttpEvent.Success(MSServerOkResponse, value);
    const successEventObservable = of(msSuccessEvent);
    return successEventObservable;
  }
}
